import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { PendoService } from 'analytics-lib';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { IComposeMailService, IRecipient } from 'email-service-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { StringUtils } from '../../../classes/stringUtils';
import { IEntity } from '../../../interfaces/ientity';
import { AiPromptService } from '../../../services/ai-prompt.service';
import { BullhornDataService } from '../../../services/bullhorn-data.service';
import { ConfigService, IAiConfig } from '../../../services/config.service';
import { PromptProfileService } from '../../../services/prompt-profile.service';
import { SaveAsDocxService } from '../../../services/save-as-docx.service';
import { SaveNoteService } from '../../../services/saveNote.service';
import { ServiceProviderService } from '../../../services/service.provider.service';
import { IBullhornCardContext } from '../../../types/bullhorn-card-context';
import { BullhornEntityType } from '../../../types/bullhorn/bullhorn-entity-type';
import { INote } from '../../../types/bullhorn/note';
import { IClientConfig } from '../../../types/client-config';
import { IEntitySourceData } from '../../../types/IEntitySourceData';
import { IPromptProfile, PromptType } from '../../../types/prompt-profile';

// TODO - name this better
const _defaultFileType = '';
declare const tinymce: any;

@Component({
  selector: 'app-ai-result',
  templateUrl: './ai-result.component.html',
  styleUrl: './ai-result.component.scss',
  standalone: false,
})
export class AiResultComponent implements OnInit {

  static readonly HTML_TOOLBAR =
    'undo redo styles bold italic alignleft aligncenter alignright alignjustify | code | aidialog aishortcuts';

  static readonly PLAINTEXT_TOOLBAR =
    'undo redo | aidialog aishortcuts';

  protected get activeEntity() {
    let entity: BullhornEntityType = this.bullhornCardContext.cardEntityType;

    if (this.selectedPromptProfile.entityType === BullhornEntityType.JOB_SUBMISSION) {
      entity = BullhornEntityType.JOB_SUBMISSION;
    }

    return entity;
  }

  protected get editorPlugins(): string {
    return this.clientConfig?.enableAiEditorPlugin ? 'autoresize wordcount ai code' : 'autoresize wordcount code';
  }
  /* TODO: CHECK ALL THESE INPUTS - NEED TO TRANSFER THE WHOLE THING? */

  @Input() set aiResponse(aiResponse: string) {
    this._aiResponse = aiResponse;
    this.isHTMLResponse = StringUtils.isHtml(aiResponse);
    this.toolbar = this.isHTMLResponse ? AiResultComponent.HTML_TOOLBAR : AiResultComponent.PLAINTEXT_TOOLBAR;
  }

  get aiResponse(): string {
    return this._aiResponse;
  }

  @Input()
  aiTokenResponse!: string;

  @Input()
  bullhornCardContext!: IBullhornCardContext;

  @Input()
  selectedPromptProfile!: IPromptProfile; /* TODO: NEED TO TRANSFER THE WHOLE THING or just outputFielName? */

  @Input()
  entityData: IEntitySourceData[] = [];

  @Input()
  tinyApiKey!: string;

  @Input() fieldNameToSaveTo: string | undefined = '';

  @Input() clientConfig: IClientConfig;

  @Input() enableCopyButton = true;
  @Input() enableSaveAsNote = true;

  @Output() aiOptions = new EventEmitter<any>();

  @Output()
  editorViewEmit = new EventEmitter<boolean>();

  protected _aiResponse: string;
  protected toolbar: string;
  protected isHTMLResponse: boolean;

  protected readonly StringUtils = StringUtils;
  protected readonly BullhornEntityType = BullhornEntityType;
  protected editorView = false;
  protected userSelectingText = false;

  /* Timeouts */
  protected selectingTimeout: any;
  protected singleClickTimeout: any;

  protected responseTooltipDisabled = false;

  @ViewChild(MatTooltip) tooltip: MatTooltip | undefined;

  protected saveAsDocxButtonDisabled = false;

  private readonly composeMailService: IComposeMailService | undefined;

  private pendingAiRequest: { request: any, resolve: (value: string) => void } | null = null;
  private aiConfig: IAiConfig | null = null;

  protected aiShortcuts = [
    { title: 'Summarize content', prompt: 'Provide the key points and concepts in this content in a succinct summary.', selection: false },
    { title: 'Improve writing', prompt: 'Rewrite this content with no spelling mistakes, proper grammar, and with more descriptive language, using best writing practices without losing the original meaning.', selection: false },
    { title: 'Simplify language', prompt: 'Rewrite this content with simplified language and reduce the complexity of the writing, so that the content is easier to understand.', selection: false },
    { title: 'Expand upon', prompt: 'Expand upon this content with descriptive language and more detailed explanations, to make the writing easier to understand and increase the length of the content.', selection: false },
    { title: 'Trim content', prompt: 'Remove any repetitive, redundant, or non-essential writing in this content without changing the meaning or losing any key information.', selection: false },
    {
      title: 'Change tone', subprompts: [
        { title: 'Professional', prompt: 'Rewrite this content using polished, formal, and respectful language to convey professional expertise and competence.', selection: false },
        { title: 'Casual', prompt: 'Rewrite this content with casual, informal language to convey a casual conversation with a real person.', selection: false },
        { title: 'Direct', prompt: 'Rewrite this content with direct language using only the essential information.', selection: false },
        { title: 'Confident', prompt: 'Rewrite this content using compelling, optimistic language to convey confidence in the writing.', selection: false },
        { title: 'Friendly', prompt: 'Rewrite this content using friendly, comforting language, to convey understanding and empathy.', selection: false },
      ]
    },
    {
      title: 'Change style', subprompts: [
        { title: 'Business', prompt: 'Rewrite this content as a business professional with formal language.', selection: false },
        { title: 'Legal', prompt: 'Rewrite this content as a legal professional using valid legal terminology.', selection: false },
        { title: 'Journalism', prompt: 'Rewrite this content as a journalist using engaging language to convey the importance of the information.', selection: false },
        { title: 'Medical', prompt: 'Rewrite this content as a medical professional using valid medical terminology.', selection: false },
        { title: 'Poetic', prompt: 'Rewrite this content as a poem using poetic techniques without losing the original meaning.', selection: false },
      ]
    },
  ];

  constructor(
    private messageNotificationService: MessageNotificationService,
    private bullhornDataService: BullhornDataService,
    private clipboard: Clipboard,
    protected router: Router,
    private noteService: SaveNoteService,
    private saveAsDocxService: SaveAsDocxService,
    private aiPromptService: AiPromptService,
    private clientConfigService: ConfigService,
    private pendoService: PendoService,
    private promptProfileService: PromptProfileService,
    serviceProviderService: ServiceProviderService
  ) {
    // TODO implement namespace properly
    this.composeMailService = serviceProviderService.getComposeMailService('bullhorn');
  }

  ngOnInit(): void {
    this.clientConfigService.getAiConfig().subscribe({
      next: (config) => {
        this.aiConfig = config;
      },
      error: (error) => {
        console.error('Failed to load AI config:', error);
      }
    });
  }

  // Keyboard Shortcuts & Event Handlers
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    // Use Escape to close Editor view of prompt
    if (this.editorView && event.code === 'Escape') {
      this.closeEditorView();
    }
  }

  @HostListener('document:keydown.control.c', ['$event'])
  @HostListener('document:keydown.meta.c', ['$event'])
  protected handleCopyKeyboard(event: KeyboardEvent): void {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      this.trackCopyAction('Ctrl+C');
    }
  }

  private createAiRequest(query: string, context: string): Promise<string> {
    const promptProfile = {
      ...this.selectedPromptProfile,
      action: query,
      description: this.aiResponse,
      promptType: PromptType.AI_ADDON_PROMPT,
    };

    const entityData = this.entityData.map((entity) => ({
      ...entity,
      'user-input-text': this.extractTextFromHtml(context),
    }));

    const config = this.aiConfig;

    return new Promise((resolve) => {
      this.aiPromptService.sendAiRequest(
        entityData,
        this.promptProfileService.createPromptRequest(promptProfile),
        config,
        [],
        false,
        [],
        this.bullhornCardContext,
        {
          handleQuestion(): void {},
          handleMessage: (message: any) => {
            if (message.type === 'AiServiceResponse') {
              const aiResponse = message.message.message || 'AI response generated';
              resolve(aiResponse);
            }
          },
          handleError: () => resolve('Error processing AI request')
        }
      );
    });
  }

  protected sendAiOptions(): void {
    if (this.pendingAiRequest) {
      const { request, resolve } = this.pendingAiRequest;
      this.createAiRequest(request.query, request.context).then(resolve);
      this.pendingAiRequest = null;
    }
    this.aiOptions.emit(this.aiOptions);
  }

  protected editorChanged($event: any): void {
    this.aiResponse = $event;
  }

  protected showResponseTooltip() {
    this.responseTooltipDisabled = false;
    setTimeout(() => {
      this.responseTooltipDisabled = true;
    }, 5000);
  }

  protected hideResponseTooltip() {
    this.responseTooltipDisabled = true;
  }

  protected closeEditorView(): void {
    this.editorView = false;
    this.editorViewEmit.emit(this.editorView);
  }

  private getEmailDestinationEntity(): Observable<IEntity | null> {
    switch (this.selectedPromptProfile.entityType) {
      case BullhornEntityType.JOB_ORDER:
      case BullhornEntityType.OPPORTUNITY:
        return this.bullhornDataService
          .getFields(
            this.bullhornCardContext.cardEntityType,
            this.bullhornCardContext.cardEntityId,
            ['clientContact(id)']
          )
          .pipe(
            map((bhEntity) => {
              return {
                id: (bhEntity.data as any)?.clientContact?.id as string,
                type: BullhornEntityType.CLIENT_CONTACT,
              };
            })
          );

      case BullhornEntityType.JOB_SUBMISSION:
      case BullhornEntityType.PLACEMENT:
      case BullhornEntityType.CLIENT_CORPORATION:
        return of({ id: null, type: null });

      default:
        return of({
          id: this.bullhornCardContext.cardEntityId.toString(),
          type: this.bullhornCardContext.cardEntityType,
        });
    }
  }

  protected sendBullhornEmail(response: string) {
    this.getEmailDestinationEntity().subscribe((entity) => {
      this.trackEmailComposedAction(entity);

      const recipients = this.getRecipientId(entity);
      this.composeMailService?.launch(
        entity,
        { source: entity, destinations: [entity] },
        [],
        'Follow-up from our recent conversation',
        recipients,
        response
      );
    });
  }

  protected responseToClipboard(): void {
    // DOUBLE CLICK EVENT
    clearTimeout(this.singleClickTimeout);
    clearTimeout(this.selectingTimeout);
    this.copyContentWithFormatting(this.aiResponse);
    this.trackCopyAction('Double click to Copy');
    this.success('Copied to clipboard');
  }

  protected copyToClipboard(): void {
    this.copyContentWithFormatting(this.aiResponse);
    this.trackCopyAction('Copy Button');
    this.success('Copied to clipboard');
  }

  private copyContentWithFormatting(content: string): void {
    if (!content) {
      return;
    }
    const isHtml = StringUtils.isHtml(content);
    if (isHtml) {
      const styledHtml = this.addInlineStylesToHtml(content);
      this.copyHtmlToClipboard(styledHtml);
    } else {
      this.clipboard.copy(content);
    }
  }

  private copyHtmlToClipboard(html: string): void {
    const plainText = this.extractTextFromHtml(html);
    const policy = (document as any).permissionsPolicy ?? (document as any).featurePolicy;
    const clipboardWriteAllowed = policy?.allowsFeature('clipboard-write') ?? true;

    if (clipboardWriteAllowed && navigator.clipboard && typeof ClipboardItem !== 'undefined') {
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      navigator.clipboard
        .write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })])
        .catch(() => this.execCommandCopyHtml(html, plainText));
    } else {
      this.execCommandCopyHtml(html, plainText);
    }
  }

  // Fallback for cross-origin iframes where navigator.clipboard.write() is blocked.
  // execCommand is deprecated but remains functional and does not require clipboard-write permission.
  private execCommandCopyHtml(html: string, plainText: string): void {
    const tempDiv = document.createElement('div');
    tempDiv.contentEditable = 'true';
    tempDiv.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
    tempDiv.innerHTML = html;
    document.body.appendChild(tempDiv);

    const range = document.createRange();
    range.selectNodeContents(tempDiv);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
      try {
        document.execCommand('copy');
      } catch {
        this.clipboard.copy(plainText);
      }
      selection.removeAllRanges();
    }

    document.body.removeChild(tempDiv);
  }

  private addInlineStylesToHtml(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(el => {
      (el as HTMLElement).style.fontWeight = 'bold';
    });

    doc.querySelectorAll('strong, b').forEach(el => {
      (el as HTMLElement).style.fontWeight = 'bold';
    });

    doc.querySelectorAll('em, i').forEach(el => {
      (el as HTMLElement).style.fontStyle = 'italic';
    });

    const styledContent = doc.body.innerHTML;
    const htmlFragment = `<!--StartFragment-->${styledContent}<!--EndFragment-->`;

    return `<!DOCTYPE html>
            <html>
            <head>
            <meta charset="utf-8">
            <meta name="Generator" content="Kyloe AI Assist">
            </head>
            <body>
            ${htmlFragment}
            </body>
            </html>`;
  }

  protected checkIfUserIsSelectingText(): void {
    this.userSelectingText = false;
    this.selectingTimeout = setTimeout(() => {
      this.userSelectingText = true;
    }, 251);
  }

  protected showEditor(mouseButton?: any): void {
    if (mouseButton === 0 || !mouseButton) {
      // Allow to right click
      // Start the up with a clean timer slate!
      clearTimeout(this.singleClickTimeout);
      clearTimeout(this.selectingTimeout);

      this.singleClickTimeout = setTimeout(() => {
        if (!this.userSelectingText) {
          clearTimeout(this.selectingTimeout);
          this.editorView = true;
          this.editorViewEmit.emit(this.editorView);
        }
      }, 250);
    }
  }

  protected aiRequestHandler = (request: any, respondWith: any) => {
    const context = request.context || this.aiResponse;
    respondWith.string(() => this.createAiRequest(request.query, context));
  }

  protected translateAIAssistantText(): string {
    if (typeof tinymce !== 'undefined' && tinymce.addI18n) {
      return tinymce.addI18n('en', {
        'AI Assistant': 'Edit output with AI (GPT-4o mini)',
        'Insert': 'Replace'
      });
    }
  }

  private extractTextFromHtml(content: string): string {
    return content?.replace(/<[^>]*>/g, '').trim() || '';
  }

  protected success(message: string): void {
    this.messageNotificationService.success(message);
  }

  private trackCopyAction(copyMethod: string): void {
    const sourceEntity = this.getSourceEntity();
    const separatedIds = this.getSeparatedEntityIds();

    const pendoEventData = {
      promptId: this.pendoService.truncateForPendo(this.selectedPromptProfile?.promptId),
      promptName: this.pendoService.truncateForPendo(this.selectedPromptProfile?.displayName),
      promptEntityType: this.pendoService.truncateForPendo(this.selectedPromptProfile?.entityType),
      uiEntityType: this.pendoService.truncateForPendo(this.bullhornCardContext?.cardEntityType),
      uiEntityId: this.bullhornCardContext?.cardEntityId,
      entityForGeneratingPromptType: this.pendoService.truncateForPendo(sourceEntity.type),
      entityForGeneratingPromptId: separatedIds.entityForGeneratingPromptId,
      candidateEntityForGeneratingPromptId: separatedIds.candidateEntityForGeneratingPromptId,
      copyMethod: copyMethod,
      enableCopyButton: this.enableCopyButton,
      enableSaveAsNote: this.enableSaveAsNote
    };

    this.pendoService?.recordEvent('AIA Card | Copy Action', pendoEventData);
  }

  private trackSaveToFieldAction(fieldName: string | undefined): void {
    const sourceEntity = this.getSourceEntity();
    const separatedIds = this.getSeparatedEntityIds();

    const pendoEventData = {
      promptId: this.pendoService.truncateForPendo(this.selectedPromptProfile?.promptId),
      promptName: this.pendoService.truncateForPendo(this.selectedPromptProfile?.displayName),
      promptEntityType: this.pendoService.truncateForPendo(this.selectedPromptProfile?.entityType),
      uiEntityType: this.pendoService.truncateForPendo(this.bullhornCardContext?.cardEntityType),
      uiEntityId: this.bullhornCardContext?.cardEntityId,
      entityForGeneratingPromptType: this.pendoService.truncateForPendo(sourceEntity.type),
      entityForGeneratingPromptId: separatedIds.entityForGeneratingPromptId,
      candidateEntityForGeneratingPromptId: separatedIds.candidateEntityForGeneratingPromptId,
      destinationEntityType: this.pendoService.truncateForPendo(this.bullhornCardContext?.cardEntityType),
      destinationEntityId: this.bullhornCardContext?.cardEntityId,
      fieldName: this.pendoService.truncateForPendo(fieldName),
      enableCopyButton: this.enableCopyButton,
      enableSaveAsNote: this.enableSaveAsNote
    };

    this.pendoService?.recordEvent('AIA Card | Save to Field', pendoEventData);
  }

  private trackNoteCreatedAction(): void {
    const sourceEntity = this.getSourceEntity();
    const separatedIds = this.getSeparatedEntityIds();
    const promptEntityType = this.selectedPromptProfile?.entityType;

    let destinationEntityType: string | null;
    if (promptEntityType === BullhornEntityType.CANDIDATE ||
      promptEntityType === BullhornEntityType.JOB_SUBMISSION ||
      promptEntityType === BullhornEntityType.PLACEMENT) {
      destinationEntityType = BullhornEntityType.CANDIDATE;
    } else if (promptEntityType === BullhornEntityType.CLIENT_CONTACT ||
      promptEntityType === BullhornEntityType.JOB_ORDER ||
      promptEntityType === BullhornEntityType.OPPORTUNITY) {
      destinationEntityType = BullhornEntityType.CLIENT_CONTACT;
    } else if (promptEntityType === BullhornEntityType.LEAD) {
      destinationEntityType = BullhornEntityType.LEAD;
    } else {
      destinationEntityType = null;
    }

    // personReference IDs
    let destinationEntityId: string | number;
    if (promptEntityType === BullhornEntityType.JOB_SUBMISSION) {
      destinationEntityId = separatedIds.candidateEntityForGeneratingPromptId || '';
    } else if (promptEntityType === BullhornEntityType.CANDIDATE ||
      promptEntityType === BullhornEntityType.CLIENT_CONTACT ||
      promptEntityType === BullhornEntityType.LEAD) {
      destinationEntityId = this.bullhornCardContext?.cardEntityId;
    } else {
      destinationEntityId = this.bullhornCardContext?.cardEntityId;
    }

    const pendoEventData = {
      promptId: this.pendoService.truncateForPendo(this.selectedPromptProfile?.promptId),
      promptName: this.pendoService.truncateForPendo(this.selectedPromptProfile?.displayName),
      promptEntityType: this.pendoService.truncateForPendo(this.selectedPromptProfile?.entityType),
      uiEntityType: this.pendoService.truncateForPendo(this.bullhornCardContext?.cardEntityType),
      uiEntityId: this.bullhornCardContext?.cardEntityId,
      entityForGeneratingPromptType: this.pendoService.truncateForPendo(sourceEntity.type),
      entityForGeneratingPromptId: separatedIds.entityForGeneratingPromptId,
      candidateEntityForGeneratingPromptId: separatedIds.candidateEntityForGeneratingPromptId,
      destinationEntityType: this.pendoService.truncateForPendo(destinationEntityType),
      destinationEntityId: destinationEntityId,
      enableCopyButton: this.enableCopyButton,
      enableSaveAsNote: this.enableSaveAsNote
    };

    this.pendoService?.recordEvent('AIA Card | Note Created', pendoEventData);
  }

  private trackDocxGeneratedAction(fileName: string | undefined): void {
    const sourceEntity = this.getSourceEntity();
    const separatedIds = this.getSeparatedEntityIds();

    const pendoEventData = {
      promptId: this.pendoService.truncateForPendo(this.selectedPromptProfile?.promptId),
      promptName: this.pendoService.truncateForPendo(this.selectedPromptProfile?.displayName),
      promptEntityType: this.pendoService.truncateForPendo(this.selectedPromptProfile?.entityType),
      uiEntityType: this.pendoService.truncateForPendo(this.bullhornCardContext?.cardEntityType),
      uiEntityId: this.bullhornCardContext?.cardEntityId,
      entityForGeneratingPromptType: this.pendoService.truncateForPendo(sourceEntity.type),
      entityForGeneratingPromptId: separatedIds.entityForGeneratingPromptId,
      candidateEntityForGeneratingPromptId: separatedIds.candidateEntityForGeneratingPromptId,
      destinationEntityType: this.pendoService.truncateForPendo(this.bullhornCardContext?.cardEntityType),
      destinationEntityId: this.bullhornCardContext?.cardEntityId,
      fileName: this.pendoService.truncateForPendo(fileName),
      enableCopyButton: this.enableCopyButton,
      enableSaveAsNote: this.enableSaveAsNote
    };

    this.pendoService?.recordEvent('AIA Card | DOCX Generated', pendoEventData);
  }

  private trackEmailComposedAction(destinationEntity: IEntity | null): void {
    const sourceEntity = this.getSourceEntity();
    const separatedIds = this.getSeparatedEntityIds();

    const pendoEventData = {
      promptId: this.pendoService.truncateForPendo(this.selectedPromptProfile?.promptId),
      promptName: this.pendoService.truncateForPendo(this.selectedPromptProfile?.displayName),
      promptEntityType: this.pendoService.truncateForPendo(this.selectedPromptProfile?.entityType),
      uiEntityType: this.pendoService.truncateForPendo(this.bullhornCardContext?.cardEntityType),
      uiEntityId: this.bullhornCardContext?.cardEntityId,
      entityForGeneratingPromptType: this.pendoService.truncateForPendo(sourceEntity.type),
      entityForGeneratingPromptId: separatedIds.entityForGeneratingPromptId,
      candidateEntityForGeneratingPromptId: separatedIds.candidateEntityForGeneratingPromptId,
      destinationEntityType: this.pendoService.truncateForPendo(destinationEntity?.type),
      destinationEntityId: destinationEntity?.id,
      enableCopyButton: this.enableCopyButton,
      enableSaveAsNote: this.enableSaveAsNote
    };

    this.pendoService?.recordEvent('AIA Card | Email Composed', pendoEventData);
  }

  private getSourceEntity(): { type: string | null; id: string | null } {
    if (!this.entityData?.length) {
      return { type: null, id: null };
    }

    const types: string[] = [];
    const ids: string[] = [];

    for (const entity of this.entityData) {
      const entityType = entity.sourcePathCommand?.path?.[0];
      if (entityType && !types.includes(entityType)) {
        types.push(entityType);
      }
      if (entity.id && !ids.includes(entity.id)) {
        ids.push(entity.id);
      }
    }

    const toCommaString = (arr: string[]) =>
      arr.length > 0 ? arr.join(', ') : null;

    return {
      type: toCommaString(types),
      id: toCommaString(ids)
    };
  }

  private getSeparatedEntityIds(): {
    entityForGeneratingPromptId: string | null;
    candidateEntityForGeneratingPromptId: string | null;
  } {
    if (!this.entityData?.length) {
      return {
        entityForGeneratingPromptId: null,
        candidateEntityForGeneratingPromptId: null
      };
    }

    const candidateIds: string[] = [];
    const jobOrderIds: string[] = [];
    const otherIds: string[] = [];

    for (const entity of this.entityData) {
      const entityType = entity.sourcePathCommand?.path?.[0];
      if (entityType === 'Candidate' && entity.id && !candidateIds.includes(entity.id)) {
        candidateIds.push(entity.id);
      } else if (entityType === 'JobOrder' && entity.id && !jobOrderIds.includes(entity.id)) {
        jobOrderIds.push(entity.id);
      } else if (entity.id && !otherIds.includes(entity.id)) {
        otherIds.push(entity.id);
      }
    }

    const isJobSubmission = this.selectedPromptProfile?.entityType === BullhornEntityType.JOB_SUBMISSION;

    if (isJobSubmission) {
      // For JobSubmission: use JobOrder IDs, Candidate IDs are separate
      return {
        entityForGeneratingPromptId: jobOrderIds.length > 0 ? jobOrderIds.join(', ') : null,
        candidateEntityForGeneratingPromptId: candidateIds.length > 0 ? candidateIds.join(', ') : null
      };
    } else {
      // For non-JobSubmission: use regular IDs, blank candidate field
      const allIds = [...candidateIds, ...jobOrderIds, ...otherIds];
      return {
        entityForGeneratingPromptId: allIds.length > 0 ? allIds.join(', ') : null,
        candidateEntityForGeneratingPromptId: null
      };
    }
  }

  private formatPutData(putData: INote) {
    return `{
        "comments": ${JSON.stringify(putData.comments)},
        "personReference": {
          "id": ${putData.personReference}
        },
        "action": "${putData.action}",
        "jobOrders": [{
            "id": ${putData.jobOrders}
          }],
        "opportunities": [{
          "id": ${putData.opportunities}
        }],
        "placements": [{
          "id": ${putData.placements}
        }]
      }
    `;
  }

  protected createBullhornNote(content: string) {
    const putData: INote = {
      action: 'Kyloe AI Assist Result',
      comments: content,
      personReference: 0,
      jobOrders: 0,
      opportunities: 0,
      placements: 0,
    };

    this.trackNoteCreatedAction();

    if (this.selectedPromptProfile.entityType === BullhornEntityType.JOB_SUBMISSION) {
      this.createJobSubmissionNote(putData);
    } else if (this.selectedPromptProfile.entityType === BullhornEntityType.JOB_ORDER) {
      this.createJobOrderNote(putData);
    } else if (this.selectedPromptProfile.entityType === BullhornEntityType.OPPORTUNITY) {
      this.createOpportunityNote(putData);
    } else if (this.selectedPromptProfile.entityType === BullhornEntityType.PLACEMENT) {
      this.createPlacementNote(putData);
    } else if (
      this.activeEntity === BullhornEntityType.CANDIDATE ||
      this.activeEntity === BullhornEntityType.CLIENT_CONTACT ||
      this.activeEntity === BullhornEntityType.LEAD
    ) {
      putData.personReference = this.bullhornCardContext.cardEntityId;
      this.saveAsNote(this.formatPutData(putData));
    }
  }

  protected saveAsNote(putData: string): void {
    console.log('PUT DATA', putData);
    this.noteService.saveNote(putData, 'Saved as note', 'Failed to save note').subscribe();
  }

  // TODO refactor this into List of Candidates and Jobs
  protected createJobSubmissionNote(putData: INote) {
    const personIds: any[] = [];
    const jobOrderIds: any[] = [];
    this.entityData.forEach((entity) => {
      if (entity.sourcePathCommand.path[0] === 'Candidate') {
        if (!personIds.includes(entity.id)) {
          personIds.push(entity.id);
        }
      } else if (entity.sourcePathCommand.path[0] === 'JobOrder') {
        if (!jobOrderIds.includes(entity.id)) {
          jobOrderIds.push(entity.id);
        }
      }
    });

    const noteLocation: number[][] = personIds
      .map((str1) => jobOrderIds.map((str2) => [str1, str2]))
      .flat();
    const obs$: Observable<any>[] = [];
    noteLocation.forEach((note) => {
      putData.personReference = note[0];
      putData.jobOrders = note[1];
      obs$.push(this.bullhornDataService.createNote(this.formatPutData(putData)));
    });
    forkJoin(obs$).subscribe({
      next: (response: any) => {
        if (response && response.error) {
          console.log(response);
          return this.messageNotificationService.error('Failed to save note', response.error.errorMessage);
        } else {
          return this.messageNotificationService.success('Saved as note');
        }
      },
      error: (e) => {
        console.log(e);
        return this.messageNotificationService.error('Failed to save note', e.message);
      },
    });
    return;
  }

  protected createJobOrderNote(putData: INote) {
    putData.jobOrders = this.bullhornCardContext.cardEntityId;
    this.bullhornDataService
      .getFields(BullhornEntityType.JOB_ORDER, putData.jobOrders, ['clientContact'])
      .subscribe((fieldResult: any) => {
        console.log(fieldResult);
        putData.personReference = fieldResult.data.clientContact.id as number;
        this.saveAsNote(this.formatPutData(putData));
      });
    return;
  }

  protected createOpportunityNote(putData: INote) {
    putData.opportunities = this.bullhornCardContext.cardEntityId;
    this.bullhornDataService
      .getFields(BullhornEntityType.OPPORTUNITY, putData.opportunities, ['clientContact'])
      .subscribe((fieldResult: any) => {
        console.log(fieldResult);
        putData.personReference = fieldResult.data.clientContact.id as number;
        this.saveAsNote(this.formatPutData(putData));
      });
    return;
  }

  protected createPlacementNote(putData: INote) {
    putData.placements = this.bullhornCardContext.cardEntityId;
    this.bullhornDataService
      .getFields(BullhornEntityType.PLACEMENT, putData.placements, ['candidate, jobOrder'])
      .subscribe((fieldResult: any) => {
        console.log(fieldResult);
        putData.personReference = fieldResult.data.candidate.id as number;
        putData.jobOrders = fieldResult.data.jobOrder.id as number;
        this.saveAsNote(this.formatPutData(putData));
      });
    return;
  }

  protected saveResult(selectedPromptProfile: IPromptProfile | undefined, response: any): void {
    const dataMap = new Map();
    const saveTo = selectedPromptProfile?.outputFieldName;
    dataMap.set('id', this.bullhornCardContext.cardEntityId);
    dataMap.set(saveTo, response);

    const postData = Object.fromEntries(dataMap);
    const entityType = this.selectedPromptProfile?.entityType as BullhornEntityType;
    const entityId = this.bullhornCardContext.cardEntityId as number;

    this.trackSaveToFieldAction(saveTo);

    this.bullhornDataService.updateEntity(entityType, entityId, postData).subscribe((result) => {
      const data = result.data as Object;
      console.log(data);

      this.messageNotificationService.success(
        `Saved to field on ${entityType} as specified in Prompt Profile.`
      );
    });
  }

  protected createWordDocx(content: string) {
    const taskComplete = () => (this.saveAsDocxButtonDisabled = false);

    this.trackDocxGeneratedAction(this.selectedPromptProfile.fileConfig?.fileName);

    this.saveAsDocxButtonDisabled = true;
    this.saveAsDocxService.execute(
      {
        inputText: content,
        outputFileName: this.selectedPromptProfile.fileConfig!.fileName,
        fileType: this.selectedPromptProfile.fileConfig?.fileType || _defaultFileType,
        entity: {
          id: this.bullhornCardContext.cardEntityId.toString(),
          type: this.bullhornCardContext.cardEntityType,
        },
      },
      taskComplete
    );
  }

  protected getRecipientId(entity: IEntity): IRecipient[] {
    if (
      entity.type === BullhornEntityTypes.Candidate ||
      entity.type === BullhornEntityTypes.ClientContact ||
      entity.type === BullhornEntityTypes.Lead
    ) {
      return [{ id: parseInt(entity.id, 10) }];
    }
    return [];
  }

  protected setupEditor = (editor: any) => {
    editor.on('BeforeExecCommand', (e: any) => {
      // When AI content is about to be inserted, clear existing content first
      if (e.command === 'mceInsertContent' && e.value && typeof e.value === 'string') {
        editor.setContent('');
      }
    });

    editor.on('Copy', (e: any) => {
      this.trackCopyAction('Ctrl+C (From Editor)');
    });
  }
}
